import React, { useEffect, useState, useCallback } from "react";
import { dispatchBreakersAPI } from "../../services/api/admin";

const POLL_INTERVAL_MS = 15000;

const LANE_LABELS = {
  call: "Call",
  evaluation: "Evaluation",
};

const QUEUED_LABELS = {
  call: "Queued (held) runs",
  evaluation: "Pending evaluations",
};

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function AdminDispatchBreakers() {
  const [breakers, setBreakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [resuming, setResuming] = useState({});

  const showMsg = (type, text) => {
    setMsg({ type, text });
    if (text) setTimeout(() => setMsg({ type: "", text: "" }), 4000);
  };

  const load = useCallback(async (showLoader = false) => {
    if (showLoader) setLoading(true);
    try {
      const data = await dispatchBreakersAPI.list();
      setBreakers(data || []);
    } catch (e) {
      showMsg("error", e.response?.data?.error || "Failed to load dispatch breakers");
    } finally {
      if (showLoader) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(true);
    const interval = setInterval(() => load(false), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [load]);

  const handleResume = async (lane) => {
    if (!window.confirm(`Resume ${LANE_LABELS[lane] || lane} dispatch? Queued work will begin processing immediately.`)) return;
    setResuming((prev) => ({ ...prev, [lane]: true }));
    try {
      await dispatchBreakersAPI.resume(lane);
      showMsg("success", `${LANE_LABELS[lane] || lane} dispatch resumed`);
      await load(false);
    } catch (e) {
      showMsg("error", e.response?.data?.error || "Resume failed");
    } finally {
      setResuming((prev) => ({ ...prev, [lane]: false }));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Dispatch Breakers</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Per-lane circuit breakers — tripped when a vendor returns a rate-limit error. Resume to release held work.
          </p>
        </div>
      </div>

      {msg.text && (
        <div
          className={`mb-4 rounded-lg px-4 py-2.5 text-sm ${
            msg.type === "success"
              ? "bg-green-50 text-green-700 border border-green-200"
              : "bg-red-50 text-red-600 border border-red-100"
          }`}
        >
          {msg.text}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground py-8 text-center">Loading...</div>
      ) : breakers.length === 0 ? (
        <div className="text-sm text-muted-foreground py-8 text-center">No dispatch breakers found.</div>
      ) : (
        <div className="grid gap-4">
          {breakers.map((breaker) => {
            const isPaused = breaker.state === "paused";
            const laneLabel = LANE_LABELS[breaker.lane] || breaker.lane;
            const queuedLabel = QUEUED_LABELS[breaker.lane] || "Queued";
            const isResuming = !!resuming[breaker.lane];

            return (
              <div
                key={breaker.lane}
                className="bg-card rounded-lg border border-border p-5"
              >
                {/* Card header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <h2 className="text-base font-semibold text-foreground">{laneLabel} Lane</h2>
                    <span
                      className={`px-2.5 py-0.5 inline-flex text-xs font-medium rounded-full ${
                        isPaused
                          ? "bg-red-50 text-red-700 border border-red-200"
                          : "bg-green-50 text-green-700 border border-green-200"
                      }`}
                    >
                      {isPaused ? "Paused" : "Active"}
                    </span>
                  </div>
                  <button
                    onClick={() => handleResume(breaker.lane)}
                    disabled={!isPaused || isResuming}
                    className="px-4 py-2 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {isResuming ? "Resuming..." : "Resume dispatch"}
                  </button>
                </div>

                {/* Details grid */}
                <div className="grid grid-cols-2 gap-x-8 gap-y-2 mb-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">{queuedLabel}: </span>
                    <span className="font-medium text-foreground">{breaker.queued_runs ?? 0}</span>
                  </div>

                  {isPaused && (
                    <>
                      <div>
                        <span className="text-muted-foreground">Tripped by: </span>
                        <span className="font-medium text-foreground">{breaker.tripped_by_vendor || "—"}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Error code: </span>
                        <span className="font-medium text-foreground">
                          {breaker.tripped_code != null ? String(breaker.tripped_code) : "—"}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Tripped at: </span>
                        <span className="font-medium text-foreground">{fmtTime(breaker.tripped_at)}</span>
                      </div>
                    </>
                  )}

                  {!isPaused && breaker.resumed_at && (
                    <div>
                      <span className="text-muted-foreground">Last resumed: </span>
                      <span className="font-medium text-foreground">{fmtTime(breaker.resumed_at)}</span>
                    </div>
                  )}
                </div>

                {/* Recent events */}
                <div>
                  <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                    Recent vendor-limit events
                  </h3>
                  {!breaker.recent_events || breaker.recent_events.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No recent events.</p>
                  ) : (
                    <div className="bg-card rounded-lg border border-border overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Vendor</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Code</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Message</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Time</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {breaker.recent_events.map((evt, idx) => (
                            <tr key={idx} className="hover:bg-muted/50">
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-foreground">{evt.vendor || "—"}</td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-foreground">
                                {evt.code != null ? String(evt.code) : "—"}
                              </td>
                              <td className="px-4 py-2 text-sm text-foreground max-w-sm truncate">{evt.message || "—"}</td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-muted-foreground">{fmtTime(evt.at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
